// @flow
import React, {Component} from 'react';
import GuestPicker from '@homeaway/react-guest-picker';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {sumBy, toNumber} from 'lodash';
import {SVGIcon} from '@homeaway/react-svg';
import {media} from '../../utils/breakpoints';
import PickerInputLabel from '../PickerInputLabel/PickerInputLabel';
import type {Element} from 'react';
import type {State as ReduxState} from '../../reducers';
import {injectIntl, type IntlShape} from 'react-intl';
import {MIN_ADULTS} from '../../utils/constants';

type MessageDescriptor = {messageId: string, defaultText: string};

type StateProps = {
    isTouchDevice: boolean
};

type OwnProps = {
    isOpen: boolean,
    petIncluded: boolean,
    adultsCount: number,
    childrenCount: number,
    guestsIcon?: Element<typeof SVGIcon>,
    noGuestDescriptor?: MessageDescriptor,
    hasGuestDescriptor?: MessageDescriptor
};

type Props = StateProps & OwnProps & {intl: IntlShape};

// todo shared with condensed search bar, need to export this from shared lib
const guestCount = (adultsCount = 0, childrenCount = 0) =>
    sumBy([adultsCount, childrenCount], toNumber);

const guestsText = ({adultsCount, childrenCount, petIncluded, intl}) => {
    const {formatMessage} = intl;
    const sum = guestCount(adultsCount, childrenCount);
    if (sum > 0) {
        return formatMessage(
            {
                id: 'condensedSearchBarGuests',
                defaultMessage:
                    '{sum, number} {sum, plural, one {guest} other {guests}}{pets, select, yes {, pets} no {}}'
            },
            {sum, pets: petIncluded ? 'yes' : 'no'}
        );
    }
    return '';
};

export const guestsDescriptor = {
    messageId: 'guestsPickerLabel',
    defaultText: 'Guests'
};

class ConnectedGuestPicker extends Component<Props> {
    static defaultProps = {
        noGuestDescriptor: guestsDescriptor,
        hasGuestDescriptor: guestsDescriptor
    };

    petsLabel = () => {
        const {formatMessage} = this.props.intl;
        return formatMessage({
            id: 'guestsPickerPetLabel',
            defaultMessage: 'Pets'
        });
    };

    petsInfoLabel = () => {
        const {
            intl: {formatMessage},
            petIncluded
        } = this.props;
        if (petIncluded) {
            return formatMessage({
                id: 'guestsPickerPetsMessage',
                defaultMessage: 'Only properties that allow pets will be shown'
            });
        }
        return null;
    };

    petYesLabel = () => {
        const {
            intl: {formatMessage}
        } = this.props;
        return formatMessage({
            id: 'petYesLabel',
            defaultMessage: 'Yes'
        });
    };

    petNoLabel = () => {
        const {
            intl: {formatMessage}
        } = this.props;
        return formatMessage({
            id: 'petNoLabel',
            defaultMessage: 'No'
        });
    };

    guestLabel = () => {
        const {
            intl: {formatMessage}
        } = this.props;
        return formatMessage({
            id: 'guestsPickerLabel',
            defaultMessage: 'Guests'
        });
    };

    childrenLabel = () => {
        const {
            intl: {formatMessage},
            childrenCount = 0
        } = this.props;
        return formatMessage(
            {
                id: 'guestsPickerChildrenLabel',
                defaultMessage:
                    '{childrenCount, number} {childrenCount, plural, one {Child} other {Children}}'
            },
            {childrenCount}
        );
    };

    adultsLabel = () => {
        const {
            intl: {formatMessage},
            adultsCount = 0
        } = this.props;
        return formatMessage(
            {
                id: 'guestsPickerAdultsLabel',
                defaultMessage:
                    '{adultsCount, number} {adultsCount, plural, one {Adult} other {Adults}}'
            },
            {adultsCount}
        );
    };

    hasGuest = () => {
        const {
            adultsCount = 0,
            childrenCount = 0,
            petIncluded = false
        } = this.props;
        return adultsCount > 0 || childrenCount > 0 || petIncluded;
    };

    continueButtonLabel = () => {
        const {formatMessage} = this.props.intl;
        return formatMessage({
            id: 'datePickerContinueLabel',
            defaultMessage: 'Continue'
        });
    };

    applyButtonLabel = () => {
        const {formatMessage} = this.props.intl;
        return formatMessage({
            id: 'formModalApplyLabel',
            defaultMessage: 'Apply'
        });
    };

    render() {
        const {
            guestsIcon,
            isTouchDevice,
            hasGuestDescriptor,
            noGuestDescriptor,
            adultsCount,
            childrenCount,
            petIncluded,
            intl,
            isOpen,
            ...rest
        } = this.props;

        const guestTextDescriptor = this.hasGuest()
            ? hasGuestDescriptor
            : noGuestDescriptor;

        return (
            <GuestPicker
                {...rest}
                className="GuestPicker"
                disableTextInput={isTouchDevice}
                label={<PickerInputLabel {...guestTextDescriptor} />}
                modalTitle={this.guestLabel()}
                guestLabel={guestsText({
                    adultsCount,
                    childrenCount,
                    petIncluded,
                    intl
                })}
                continueButtonLabel={this.continueButtonLabel()}
                petYesLabel={this.petYesLabel()}
                petNoLabel={this.petNoLabel()}
                name={'Guests'}
                adultsLabel={this.adultsLabel()}
                adultsCount={adultsCount}
                adultsMin={MIN_ADULTS}
                applyButtonLabel={this.applyButtonLabel()}
                childrenLabel={this.childrenLabel()}
                childrenCount={childrenCount}
                petIncluded={petIncluded}
                showPetOption
                formSuffixElement={this.petsInfoLabel}
                petLabel={this.petsLabel()}
                showClearButton={false}
                inputIcon={guestsIcon}
                isOpen={isOpen}
                id="guest-picker"
            />
        );
    }
}

const mapStateToProps = ({viewport: {breakpoint}}: ReduxState): StateProps => ({
    isTouchDevice:
        !media(breakpoint, {minWidth: 'SM_MIN'}) ||
        !media(breakpoint, {minWidth: 'MD_MIN'})
});

export default compose(
    connect(
        mapStateToProps,
        null
    ),
    injectIntl
)(ConnectedGuestPicker);
